import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { supabase, Tables } from "../utils/supabase";
import {
  ClipboardDocumentListIcon,
  ScaleIcon,
  CalendarDaysIcon,
  ExclamationTriangleIcon,
  BeakerIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import {
  format,
  startOfWeek,
  endOfWeek,
  addWeeks,
  subWeeks,
  eachDayOfInterval,
  isSameDay,
  isToday,
  isPast,
  startOfDay,
  endOfDay,
  parseISO,
} from "date-fns";
import { nl } from "date-fns/locale";

interface AgendaItem {
  id: string;
  type: "task" | "calibration" | "expiry_session" | "complaint" | "preparation";
  title: string;
  subtitle: string;
  date: Date;
  priority?: string;
  status?: string;
  color: string;
  page: string;
}

interface AgendaProps {
  onPageChange?: (page: string) => void;
}

const TYPE_COLORS: Record<AgendaItem["type"], string> = {
  task: "bg-blue-100 text-blue-700 border-blue-200",
  calibration: "bg-green-100 text-green-700 border-green-200",
  expiry_session: "bg-yellow-100 text-yellow-700 border-yellow-200",
  complaint: "bg-red-100 text-red-700 border-red-200",
  preparation: "bg-purple-100 text-purple-700 border-purple-200",
};

const TYPE_ICONS: Record<AgendaItem["type"], React.FC<{ className?: string }>> = {
  task: ClipboardDocumentListIcon,
  calibration: ScaleIcon,
  expiry_session: CalendarDaysIcon,
  complaint: ExclamationTriangleIcon,
  preparation: BeakerIcon,
};

const PRIORITY_COLORS: Record<string, string> = {
  urgent: "bg-red-500",
  high: "bg-orange-400",
  medium: "bg-yellow-400",
  low: "bg-green-400",
};

const Agenda: React.FC<AgendaProps> = ({ onPageChange }) => {
  const { selectedPharmacy } = useAuth();
  const [weekStart, setWeekStart] = useState(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 }),
  );
  const [items, setItems] = useState<AgendaItem[]>([]);
  const [loading, setLoading] = useState(true);

  const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: weekStart, end: weekEnd });

  useEffect(() => {
    if (selectedPharmacy) fetchItems();
  }, [selectedPharmacy, weekStart]);

  const fetchItems = async () => {
    if (!selectedPharmacy) return;
    setLoading(true);

    const from = startOfDay(weekStart).toISOString();
    const to = endOfDay(weekEnd).toISOString();

    try {
      const [tasksRes, expirySessRes, complaintsRes, prepsRes] =
        await Promise.all([
          supabase
            .from(Tables.TASKS)
            .select("id, title, priority, status, due_date, type")
            .eq("pharmacy_id", selectedPharmacy.id)
            .gte("due_date", from)
            .lte("due_date", to)
            .in("status", ["pending", "in_progress"])
            .order("due_date"),
          supabase
            .from(Tables.EXPIRY_CHECK_SESSIONS)
            .select("id, check_date, is_completed, session_notes")
            .eq("pharmacy_id", selectedPharmacy.id)
            .gte("check_date", from.slice(0, 10))
            .lte("check_date", to.slice(0, 10))
            .order("check_date"),
          supabase
            .from(Tables.COMPLAINTS)
            .select("id, complaint_number, severity, status, created_at")
            .eq("pharmacy_id", selectedPharmacy.id)
            .in("status", ["open", "investigating"])
            .gte("created_at", from)
            .lte("created_at", to)
            .order("created_at"),
          supabase
            .from(Tables.PREPARATIONS)
            .select("id, internal_lot_number, preparation_form, status, preparation_date")
            .eq("pharmacy_id", selectedPharmacy.id)
            .gte("preparation_date", from.slice(0, 10))
            .lte("preparation_date", to.slice(0, 10))
            .order("preparation_date"),
        ]);

      const all: AgendaItem[] = [
        ...(tasksRes.data || []).map((t) => ({
          id: t.id,
          type: "task" as const,
          title: t.title,
          subtitle: t.type,
          date: parseISO(t.due_date),
          priority: t.priority,
          status: t.status,
          color: TYPE_COLORS.task,
          page: "tasks",
        })),
        ...(expirySessRes.data || []).map((s) => ({
          id: s.id,
          type: "expiry_session" as const,
          title: "Controle vervaldata",
          subtitle: s.session_notes || "Sessieplanning",
          date: parseISO(s.check_date),
          status: s.is_completed ? "completed" : "pending",
          color: TYPE_COLORS.expiry_session,
          page: "expiry-checks",
        })),
        ...(complaintsRes.data || []).map((c) => ({
          id: c.id,
          type: "complaint" as const,
          title: c.complaint_number,
          subtitle: `Ernst: ${c.severity}`,
          date: parseISO(c.created_at),
          status: c.status,
          color: TYPE_COLORS.complaint,
          page: "complaints",
        })),
        ...(prepsRes.data || []).map((p) => ({
          id: p.id,
          type: "preparation" as const,
          title: p.internal_lot_number,
          subtitle: p.preparation_form,
          date: parseISO(p.preparation_date),
          status: p.status,
          color: TYPE_COLORS.preparation,
          page: "compounding-preparations",
        })),
      ];

      setItems(all);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  const itemsForDay = (day: Date) =>
    items.filter((item) => isSameDay(item.date, day));

  const totalThisWeek = items.length;
  const overdue = items.filter(
    (i) => isPast(i.date) && i.status !== "completed",
  ).length;

  return (
    <div className="p-6 sm:p-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Agenda</h1>
        <p className="text-gray-600 mt-1">
          Overzicht van taken, controles en bereidingen per week
        </p>
      </div>

      {/* Week navigation */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setWeekStart((w) => subWeeks(w, 1))}
            className="p-2 rounded-md border border-gray-300 bg-white hover:bg-gray-50 transition-colors"
          >
            <ChevronLeftIcon className="h-4 w-4 text-gray-600" />
          </button>
          <h2 className="text-lg font-semibold text-gray-900 min-w-[220px] text-center">
            {format(weekStart, "d MMM", { locale: nl })} –{" "}
            {format(weekEnd, "d MMM yyyy", { locale: nl })}
          </h2>
          <button
            onClick={() => setWeekStart((w) => addWeeks(w, 1))}
            className="p-2 rounded-md border border-gray-300 bg-white hover:bg-gray-50 transition-colors"
          >
            <ChevronRightIcon className="h-4 w-4 text-gray-600" />
          </button>
          <button
            onClick={() =>
              setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))
            }
            className="px-3 py-1.5 text-sm font-medium text-primary-600 border border-primary-200 rounded-md hover:bg-primary-50 transition-colors"
          >
            Deze week
          </button>
        </div>

        {/* Summary chips */}
        <div className="hidden sm:flex items-center gap-3 text-sm">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full font-medium">
            {totalThisWeek} items
          </span>
          {overdue > 0 && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-red-50 text-red-700 rounded-full font-medium">
              {overdue} verlopen
            </span>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mb-6">
        {(
          [
            ["task", "Taken"],
            ["calibration", "Kalibraties"],
            ["expiry_session", "Vervaldata controles"],
            ["complaint", "Klachten"],
            ["preparation", "Bereidingen"],
          ] as [AgendaItem["type"], string][]
        ).map(([type, label]) => (
          <span
            key={type}
            className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-full border ${TYPE_COLORS[type]}`}
          >
            {label}
          </span>
        ))}
      </div>

      {loading ? (
        <div className="animate-pulse space-y-4">
          {[...Array(7)].map((_, i) => (
            <div
              key={i}
              className="h-20 bg-gray-200 rounded-lg"
            />
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {days.map((day) => {
            const dayItems = itemsForDay(day);
            const isCurrentDay = isToday(day);

            return (
              <div
                key={day.toISOString()}
                className={`rounded-lg border transition-colors ${
                  isCurrentDay
                    ? "border-primary-300 bg-primary-50"
                    : "border-gray-200 bg-white"
                }`}
              >
                {/* Day header */}
                <div
                  className={`flex items-center px-4 py-3 ${
                    dayItems.length > 0 ? "border-b border-gray-100" : ""
                  }`}
                >
                  <div className="w-32 flex-shrink-0">
                    <span
                      className={`text-sm font-semibold ${
                        isCurrentDay ? "text-primary-700" : "text-gray-700"
                      }`}
                    >
                      {format(day, "EEEE", { locale: nl })}
                    </span>
                    <span className="ml-2 text-sm text-gray-400">
                      {format(day, "d MMM", { locale: nl })}
                    </span>
                    {isCurrentDay && (
                      <span className="ml-2 text-xs font-medium text-primary-600 bg-primary-100 px-1.5 py-0.5 rounded-full">
                        Vandaag
                      </span>
                    )}
                  </div>
                  {dayItems.length === 0 && (
                    <span className="text-xs text-gray-400">Niets gepland</span>
                  )}
                </div>

                {/* Items */}
                {dayItems.length > 0 && (
                  <div className="px-4 py-3 space-y-2">
                    {dayItems.map((item) => {
                      const Icon = TYPE_ICONS[item.type];
                      return (
                        <button
                          key={`${item.type}-${item.id}`}
                          onClick={() => onPageChange?.(item.page)}
                          className={`w-full flex items-center gap-3 px-3 py-2 rounded-md border text-left hover:opacity-90 transition-opacity ${item.color}`}
                        >
                          <Icon className="h-4 w-4 flex-shrink-0" />
                          <span className="text-sm font-medium truncate">
                            {item.title}
                          </span>
                          {item.subtitle && (
                            <span className="text-xs opacity-75 truncate hidden sm:block">
                              {item.subtitle}
                            </span>
                          )}
                          {item.priority && (
                            <span
                              className={`ml-auto flex-shrink-0 h-2 w-2 rounded-full ${
                                PRIORITY_COLORS[item.priority] ?? "bg-gray-300"
                              }`}
                              title={`Prioriteit: ${item.priority}`}
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Agenda;
